/**
 * Visiopt Dashboard value formatters.
 *
 * Rules derived from the client spec (System Rules + Language Rules):
 *  - Missing / unavailable values render as `—` (em dash), never `$0` or `0%`.
 *  - Currency: "$604" when whole, "$604.50" when fractional (two decimals).
 *  - Percentages: two decimal places by default (e.g. "0.93%").
 *  - Deltas: directional arrow + absolute percent + compare label.
 *  - Ratios: "2.50x" (two decimals, lowercase x).
 *  - Time: "2.4 hrs" (one decimal).
 *
 * All functions are null-safe. Callers pass raw numeric values or null/undefined
 * from the backend (where null now represents "data unavailable" per spec).
 */

const EMPTY = '—';

function isPresent(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value as number);
}

/** Dollar currency. `$604` if whole, `$604.50` if fractional. Null → em dash. */
export function formatMoney(value: number | null | undefined): string {
  if (!isPresent(value)) return EMPTY;
  if (Number.isInteger(value)) {
    return `$${value.toLocaleString('en-US')}`;
  }
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Integer count with thousands separators. Null → em dash. */
export function formatCount(value: number | null | undefined): string {
  if (!isPresent(value)) return EMPTY;
  return Math.round(value).toLocaleString('en-US');
}

/**
 * Percentage formatter. The input is already a percentage value
 * (e.g. 0.93 renders as "0.93%"). Null → em dash.
 */
export function formatPercentage(
  value: number | null | undefined,
  decimals = 2
): string {
  if (!isPresent(value)) return EMPTY;
  return `${value.toFixed(decimals)}%`;
}

/**
 * Period-over-period delta text with directional arrow.
 * Returns the raw string — callers apply colour via their own styles.
 *
 * Examples:
 *   formatDeltaText(12.3, 'vs prev 30 days') → "▲ 12.3% vs prev 30 days"
 *   formatDeltaText(-4.5)                    → "▼ 4.5% vs previous period"
 *   formatDeltaText(0)                       → "No change vs previous period"
 *   formatDeltaText(null)                    → "—"
 */
export function formatDeltaText(
  delta: number | null | undefined,
  compareLabel = 'vs previous period'
): string {
  if (!isPresent(delta)) return EMPTY;
  if (delta === 0) return `No change ${compareLabel}`;
  const arrow = delta > 0 ? '▲' : '▼';
  return `${arrow} ${Math.abs(delta).toFixed(1)}% ${compareLabel}`;
}

/** Ratio formatted as "2.50x". Null → em dash. */
export function formatRatio(value: number | null | undefined): string {
  if (!isPresent(value)) return EMPTY;
  return `${value.toFixed(2)}x`;
}

/** Time in hours: "2.4 hrs". Null → em dash. */
export function formatTime(hours: number | null | undefined): string {
  if (!isPresent(hours)) return EMPTY;
  return `${hours.toFixed(1)} hrs`;
}
